import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GridFiltersComponent } from '../../../shared/components/filters-grid/filters-grid.component';
import {
  FilterInputTypes,
  IFilterInput,
} from '../../../shared/interfaces/filter-input.interface';
import { TagModule } from 'primeng/tag';
import { LoaderService } from 'src/app/core/components/loader/loader.service';
import { GpsConnectivityService } from '../../../shared/services/gps-connectivity.service';
import { InputTextModule } from 'primeng/inputtext';
import { HeaderSummaryCardComponent } from 'src/app/shared/components/header-summary-card/header-summary-card.component';
import { GoogleMapComponent } from './google-map/google-map.component';
import { DeadzonesDetailsDialogComponent } from './deadzones-details-dialog/deadzones-details-dialog.component';
import { cellToBoundary } from 'h3-js';

// Extended polygon options with GPS connectivity metadata
interface GpsPolygonOptions extends google.maps.PolygonOptions {
  h3Id?: string;
  entityId?: number;
  createdAtUtc?: Date;
  connectionStatusId?: number;
}

@Component({
  selector: 'app-gps-connectivity-map',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    GridFiltersComponent,
    TagModule,
    InputTextModule,
    HeaderSummaryCardComponent,
    GoogleMapComponent,
    DeadzonesDetailsDialogComponent,
  ],
  templateUrl: './gps-connectivity.component.html',
  styleUrl: './gps-connectivity.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GpsConnectivityComponent implements OnInit {
  public filters: IFilterInput[] = [];
  public AllowUpdateFilterControls = true;
  public updatedControls: IFilterInput | IFilterInput[] = [] as any;
  public searchValue: string = '';
  public lastAppliedFilters: any = {};
  public count_dead = signal<number>(0);
  public mapPoints = signal<any[]>([]);
  public polygons = signal<GpsPolygonOptions[]>([]);
  public isDeadzonesDialogVisible = signal<boolean>(false);
  public selectedZone = signal<any>(null);
  // Map of H3 zone IDs to backend connectivity data for enriching polygons
  private readonly connectivityDataMap = new Map<
    string,
    {
      entityId: number;
      createdAtUtc: Date;
      connectionStatusId: number;
    }
  >();
  // Keep a raw, flat list of service areas to resolve state by selection
  private serviceAreasRaw: Array<{ id: number; name: string; state: string }> =
    [];
  // Google Geocoder for location-based map panning
  private geocoder: google.maps.Geocoder | null = null;

  constructor(
    private readonly service: GpsConnectivityService,
    private readonly loaderService: LoaderService,
  ) {}

  ngOnInit(): void {
    this.initFilters();
    this.loadServiceAreas();
    this.fetchData(this.lastAppliedFilters, this.searchValue);
    // Initialize geocoder when Google Maps API is loaded
    if (google?.maps?.Geocoder) {
      this.geocoder = new google.maps.Geocoder();
    }
  }

  public mapCenter = signal<google.maps.LatLngLiteral>({
    lat: 29.7604,
    lng: -95.3698,
  });
  public mapZoom = signal<number>(11);
  public defaultMapOptions: google.maps.MapOptions = {
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    minZoom: 3, // 12,
  };
  public gridResolution = 8;

  private initFilters(): void {
    this.filters = [
      {
        resultLabel: 'Date range',
        type: FilterInputTypes._dateRange,
        controlName: 'dateRange',
        placeholder: 'select date',
        resultClearable: false,
        rangeFilterReferences: [
          {
            resultLabel: '',
            type: FilterInputTypes._dateRangeFrom,
            controlName: 'startDate',
            placeholder: 'From Date',
          },
          {
            resultLabel: '',
            type: FilterInputTypes._dateRangeTo,
            controlName: 'endDate',
            placeholder: 'To Date',
          },
        ],
      },
      {
        resultLabel: 'State',
        type: FilterInputTypes._dropdown,
        controlName: 'state',
        placeholder: 'State',
        optionLabel: 'label',
        options: [
          { label: 'All', value: '' },
          { label: 'AL', value: 'AL' },
          { label: 'AK', value: 'AK' },
          { label: 'AZ', value: 'AZ' },
          { label: 'AR', value: 'AR' },
          { label: 'CA', value: 'CA' },
          { label: 'CO', value: 'CO' },
          { label: 'CT', value: 'CT' },
          { label: 'DE', value: 'DE' },
          { label: 'FL', value: 'FL' },
          { label: 'GA', value: 'GA' },
          { label: 'HI', value: 'HI' },
          { label: 'ID', value: 'ID' },
          { label: 'IL', value: 'IL' },
          { label: 'IN', value: 'IN' },
          { label: 'IA', value: 'IA' },
          { label: 'KS', value: 'KS' },
          { label: 'KY', value: 'KY' },
          { label: 'LA', value: 'LA' },
          { label: 'ME', value: 'ME' },
          { label: 'MD', value: 'MD' },
          { label: 'MA', value: 'MA' },
          { label: 'MI', value: 'MI' },
          { label: 'MN', value: 'MN' },
          { label: 'MS', value: 'MS' },
          { label: 'MO', value: 'MO' },
          { label: 'MT', value: 'MT' },
          { label: 'NE', value: 'NE' },
          { label: 'NV', value: 'NV' },
          { label: 'NH', value: 'NH' },
          { label: 'NJ', value: 'NJ' },
          { label: 'NM', value: 'NM' },
          { label: 'NY', value: 'NY' },
          { label: 'NC', value: 'NC' },
          { label: 'ND', value: 'ND' },
          { label: 'OH', value: 'OH' },
          { label: 'OK', value: 'OK' },
          { label: 'OR', value: 'OR' },
          { label: 'PA', value: 'PA' },
          { label: 'RI', value: 'RI' },
          { label: 'SC', value: 'SC' },
          { label: 'SD', value: 'SD' },
          { label: 'TN', value: 'TN' },
          { label: 'TX', value: 'TX' },
          { label: 'UT', value: 'UT' },
          { label: 'VT', value: 'VT' },
          { label: 'VA', value: 'VA' },
          { label: 'WA', value: 'WA' },
          { label: 'WV', value: 'WV' },
          { label: 'WI', value: 'WI' },
          { label: 'WY', value: 'WY' },
        ],
        enableFilters: true,
      },
      {
        resultLabel: 'Service Area',
        type: FilterInputTypes._dropdown,
        controlName: 'serviceArea',
        placeholder: 'Service Area',
        hasGroup: true,
        optionLabel: 'label',
        options: [{ label: 'All', value: '' }],
        enableFilters: true,
      },
    ];
  }

  public onApplyFilters(event: { filters: any; search: string }) {
    this.lastAppliedFilters = event.filters ?? {};
    this.searchValue = event.search ?? '';

    const filters = event.filters ?? {};

    // Geocode and pan map based on filter selection (geography doesn't filter data)
    if (filters.serviceArea?.label !== 'All') {
      const selectedLabel: string = filters.serviceArea.label;
      const selectedValue: number | string = filters.serviceArea.value;
      const matched = this.serviceAreasRaw.find((sa) => {
        if (typeof selectedValue === 'number') return sa.id === selectedValue;
        return (
          sa.name.trim().toLowerCase() ===
          (selectedLabel ?? '').trim().toLowerCase()
        );
      });
      const serviceAreaName = matched?.name ?? selectedLabel;
      const state = matched?.state ?? '';
      const address = state
        ? `${serviceAreaName}, ${state}, USA`
        : `${serviceAreaName}, USA`;

      this.geocodeAndPanMap(address, 11); // City zoom level
    }
    // handle state if service area is not selected
    if (filters.state?.label !== 'All' && !filters.serviceArea?.label) {
      // State selected: geocode "{state}, USA"
      const stateName = filters.state.label;
      const address = `${stateName}, USA`;

      this.geocodeAndPanMap(address, 6); // State zoom level
    }

    // Only fetch data if start/end dates are set
    const hasDateFilter = filters.dateRange;
    if (hasDateFilter) {
      this.fetchData(this.lastAppliedFilters, this.searchValue);
    }
  }

  /**
   * Geocode an address and pan/zoom the map to the result
   * @param address - Full address string to geocode
   * @param zoomLevel - Zoom level to apply (state=6, city=11)
   */
  private geocodeAndPanMap(address: string, zoomLevel: number): void {
    if (!this.geocoder) {
      console.warn('Geocoder not initialized');
      return;
    }

    this.geocoder.geocode(
      {
        address: address,
        componentRestrictions: { country: 'US' },
      },
      (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          // Update map center and zoom using signals
          this.mapCenter.set({ lat, lng });
          this.mapZoom.set(zoomLevel);
        } else {
          console.error('Geocoding failed:', status);
        }
      },
    );
  }

  public onClearFilters() {
    this.mapPoints.set([]);
    this.count_dead.set(0);
    this.lastAppliedFilters = {};
    this.searchValue = '';
    this.fetchData({}, '');
  }

  private fetchData(filters: any, search?: string) {
    this.loaderService.showLocal();
    const start = filters['dateRange']?.['startDate'];
    const end = filters['dateRange']?.['endDate'];
    this.service
      .getConnectivityData(
        start ? this.toUtcDateOnly(start) : undefined,
        end ? this.toUtcDateOnly(end) : undefined,
      )
      .subscribe((data) => {
        // Clear and rebuild connectivity map
        this.connectivityDataMap.clear();

        // Process each GPS connectivity record
        data.forEach((record) => {
          this.connectivityDataMap.set(record.h3ZoneId, {
            entityId: record.id,
            createdAtUtc: new Date(record.createdAtUtc),
            connectionStatusId: record.connectionStatusId,
          });
        });

        // Update service's badIdsSet with H3 IDs from backend
        this.service.badIdsSet = new Set(data.map((d) => d.h3ZoneId));

        // Update stats with count of dead zones
        this.count_dead.set(data.length);
        // Refresh viewport polygons with enriched data
        this.refreshViewportGrid();

        this.loaderService.hideLocal();
      });
  }

  // Open deadzones details dialog when a clickable polygon is clicked
  public onPolygonClick(payload: {
    event: google.maps.MapMouseEvent;
    polygon: google.maps.Polygon;
    options: google.maps.PolygonOptions;
  }) {
    // Cast to our enriched type to access custom metadata
    const opts = payload.options as GpsPolygonOptions;

    if (!opts.entityId) {
      console.warn(
        'No entity ID found for clicked polygon - cannot fetch details',
      );
      return;
    }

    // Pass enriched data to dialog
    this.selectedZone.set({
      id: opts.h3Id,
      entityId: opts.entityId,
      createdAtUtc: opts.createdAtUtc,
      connectionStatusId: opts.connectionStatusId,
      name: 'GPS Deadzone',
      severity: 'low',
      paths: opts.paths,
    });

    this.isDeadzonesDialogVisible.set(true);
  }

  private loadServiceAreas() {
    this.service.getServiceAreas().subscribe((areas) => {
      // Save raw flat list to resolve state for a selected service area later
      this.serviceAreasRaw = (areas ?? []).map((a: any) => ({
        id: a.id,
        name: a.name,
        state: a.state,
      }));
      const transformedData = areas?.reduce(
        (result: any, item: any) => {
          const existingGroup = result.find(
            (group: any) => group.label === item.state,
          );

          if (existingGroup) {
            existingGroup.items.push({
              label: item.name.trim(),
              value: item.id,
            });
          } else {
            result.push({
              label: item.state,
              items: [
                {
                  label: item.name.trim(),
                  value: item.id,
                },
              ],
            });
          }

          return result;
        },
        [] as Array<{
          label: string;
          value: number;
          items: Array<{ label: string; value: number }>;
        }>,
      );

      const options = [...transformedData];

      this.updatedControls = {
        resultLabel: 'Service Area',
        type: FilterInputTypes._dropdown,
        controlName: 'serviceArea',
        optionLabel: 'label',
        options,
        enableFilters: true,
      } as IFilterInput;
    });
  }

  private refreshViewportGrid(): void {
    const badSet = this.service.badIdsSet;

    const polys: GpsPolygonOptions[] = Array.from(badSet).map((h3Id) => {
      // nstead of cells render only bad zones
      const boundary = cellToBoundary(h3Id, true) as [number, number][];
      const path = boundary.map(([lng, lat]) => ({ lat, lng }));
      const isBad = badSet.has(h3Id);

      // Get enriched metadata from backend data
      const metadata = this.connectivityDataMap.get(h3Id);

      return {
        paths: path,
        strokeColor: isBad ? '#e74c3c' : '#2ecc71',
        strokeOpacity: 0.9,
        strokeWeight: isBad ? 2 : 1,
        fillColor: isBad ? '#e74c3c' : '#2ecc71',
        fillOpacity: isBad ? 0.25 : 0.18,
        clickable: isBad,
        h3Id: h3Id,
        entityId: metadata?.entityId,
        createdAtUtc: metadata?.createdAtUtc,
        connectionStatusId: metadata?.connectionStatusId,
      } as GpsPolygonOptions;
    });

    // Update stats based on bad vs good
    const low = polys.filter((p) => (p as any).clickable).length;
    this.count_dead.set(low);
    this.polygons.set(polys);
  }

  private toUtcDateOnly(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
